import React, { useMemo, useRef, useState } from "react";
import { Button, Empty, Segmented, Space, Table, Tag } from "antd";

import { useAuth } from "../auth/AuthProvider";
import { resolveActiveCustomerContext } from "../auth/customerContext";
import { api } from "../api/http";
import { customerUserRoleLabel } from "../customerUserRoles";
import "./forkliftsPages.css";

import {
  DashboardPageShell,
  DashboardPanel,
  DashboardPrimaryActionButton,
  DashboardRefreshButton,
  DashboardSearchInput,
} from "../components/dashboard";

const getLinkedUserEmail = (row) => (row?.email || "").trim().toLowerCase();

function LinkedRolesWidget({
  customerId,
  role,
  searchTerm: controlledSearchTerm,
  onSearchTermChange,
  onRefreshReady,
  hideDesktopToolbar = false,
}) {
  const { session } = useAuth();
  const [rows, setRows] = React.useState([]);
  const [internalSearchTerm, setInternalSearchTerm] = React.useState("");
  const searchTerm = controlledSearchTerm ?? internalSearchTerm;
  const setSearchTerm = onSearchTermChange ?? setInternalSearchTerm;
  const [selectedLinkedKey, setSelectedLinkedKey] = React.useState(null);

  const authConfig = useMemo(() => {
    let token =
      session?.accessToken ??
      session?.access_token ??
      session?.idToken ??
      session?.id_token ??
      session?.token ??
      null;

    if (!token) {
      try {
        const s = JSON.parse(localStorage.getItem("session") || "{}") || {};
        token =
          s?.accessToken ??
          s?.access_token ??
          s?.idToken ??
          s?.id_token ??
          s?.token ??
          null;
      } catch {}
    }

    if (!token) {
      token =
        localStorage.getItem("accessToken") ||
        localStorage.getItem("idToken") ||
        null;
    }

    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;

    return { headers };
  }, [session]);

  const load = React.useCallback(async () => {
    if (!customerId) {
      setRows([]);
      return;
    }

    if (!authConfig?.headers?.Authorization) {
      setRows([]);
      return;
    }

    try {
      const url = `/api/customers/${customerId}/linked${
        role ? `?role=${encodeURIComponent(role)}` : ""
      }`;

      const { data } = await api.get(url, authConfig);

      if (Array.isArray(data)) {
        const deduped = [];
        const seen = new Set();
        for (const row of data) {
          const key = `${row.user_id ?? row.customer_user_id ?? row.id ?? row.email}-${row.role}`;
          if (!seen.has(key)) {
            seen.add(key);
            deduped.push(row);
          }
        }
        setRows(deduped);
      } else {
        setRows([]);
      }
    } catch {
      setRows([]);
    }
  }, [customerId, role, authConfig]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    onRefreshReady?.(load);
  }, [load, onRefreshReady]);

  const unlink = async (row) => {
    if (!window.confirm("Are you sure you want to remove this linked user?"))
      return;

    const linkId = row.customer_user_id ?? row.link_id ?? row.id;
    if (!linkId || !customerId) return;

    if (!authConfig?.headers?.Authorization) return;

    try {
      const url = `/api/customers/${customerId}/linked/${linkId}${
        role ? `?role=${encodeURIComponent(role)}` : ""
      }`;

      await api.del(url, authConfig);
      await load();
    } catch {}
  };

  const fmtRoleLabel = customerUserRoleLabel(role);

  const filteredRows = React.useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const firstName = (r?.first_name || r?.firstName || "")
        .trim()
        .toLowerCase();
      const lastName = (r?.last_name || r?.lastName || "").trim().toLowerCase();
      const email = getLinkedUserEmail(r);
      return firstName.includes(q) || lastName.includes(q) || email.includes(q);
    });
  }, [rows, searchTerm]);

  const getRowKey = (r) =>
    r.customer_user_id ??
    r.link_id ??
    r.id ??
    `${r.email || ""}-${r.user_id || ""}`;

  React.useEffect(() => {
    if (
      selectedLinkedKey &&
      !filteredRows.some((r) => getRowKey(r) === selectedLinkedKey)
    ) {
      setSelectedLinkedKey(null);
    }
  }, [filteredRows, selectedLinkedKey]);

  const columns = [
    {
      title: "Name",
      key: "first_name",
      render: (_, r) => {
        const firstName = (r?.first_name || r?.firstName || "").trim();
        const lastName = (r?.last_name || r?.lastName || "").trim();
        return [firstName, lastName].filter(Boolean).join(" ") || "—";
      },
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (_, r) => getLinkedUserEmail(r),
    },
    {
      title: "Role",
      key: "role",
      render: () => (
        <Tag className="status-pill-tag customer-users-role-tag">
          {fmtRoleLabel}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 140,
      render: (_, r) => (
        <div className="customer-users-table-action">
          <Button
            className="customer-users-remove-button"
            onClick={() => unlink(r)}
          >
            Remove
          </Button>
        </div>
      ),
    },
  ];

  return (
    <section className="customer-users-section-panel customer-users-linked-card">
      <div
        className={`customer-users-section-header dashboard-fleet-table-toolbar maintenance-list-toolbar maintenance-list-toolbar-no-helper${hideDesktopToolbar ? " customer-users-desktop-hidden-toolbar" : ""}`}
      >
        <Space
          className="dashboard-fleet-table-controls customer-users-toolbar"
          wrap
        >
          <DashboardSearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Search ${fmtRoleLabel.toLowerCase()}s`}
            ariaLabel={`Search linked ${fmtRoleLabel.toLowerCase()}s`}
            className="dashboard-fleet-search maintenance-list-search customer-users-search"
          />
          <DashboardRefreshButton
            onClick={load}
            className="maintenance-list-refresh customer-users-refresh"
          />
        </Space>
      </div>

      <Table
        className="forklifts-page-table-wrap shared-dashboard-table customer-users-linked-table"
        rowKey={getRowKey}
        dataSource={filteredRows}
        columns={columns}
        pagination={false}
        size="middle"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={`No linked ${fmtRoleLabel.toLowerCase()}s yet. Use the invite area above to add one.`}
            />
          ),
        }}
        scroll={{ x: "max-content" }}
      />
      <div
        className="customer-users-mobile-list"
        aria-label={`Linked ${fmtRoleLabel}s`}
      >
        {filteredRows.length ? (
          filteredRows.map((r) => {
            const firstName = (r?.first_name || r?.firstName || "").trim();
            const lastName = (r?.last_name || r?.lastName || "").trim();
            const fullName =
              [firstName, lastName].filter(Boolean).join(" ") || "Unnamed user";
            return (
              <article
                key={getRowKey(r)}
                className={`customer-users-mobile-card shared-selected-card${getRowKey(r) === selectedLinkedKey ? " is-selected" : ""}`}
                onClick={() => setSelectedLinkedKey(getRowKey(r))}
              >
                <div className="customer-users-mobile-card-topline">
                  <strong>{fullName}</strong>
                  <Tag className="status-pill-tag customer-users-role-tag">
                    {fmtRoleLabel}
                  </Tag>
                </div>
                <div className="customer-users-mobile-email">
                  {getLinkedUserEmail(r) || "Email unavailable"}
                </div>
                <Button
                  className="customer-users-remove-button customer-users-mobile-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    unlink(r);
                  }}
                >
                  Remove
                </Button>
              </article>
            );
          })
        ) : (
          <Empty
            className="customer-users-mobile-empty"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No linked ${fmtRoleLabel.toLowerCase()}s yet. Use the invite area above to add one.`}
          />
        )}
      </div>
    </section>
  );
}

function InviteBlock({
  customerId,
  role,
  inviteRef,
  showContent,
  inviteWidgetProps,
}) {
  const InviteWidget = require("../InviteWidget").default;
  return (
    <section
      className={`customer-users-section-panel customer-users-invite-card${showContent ? "" : " customer-users-invite-card-hidden"}`}
    >
      <InviteWidget
        ref={inviteRef}
        customerId={customerId}
        role={role}
        showContent={showContent}
        hideSectionHeading
        {...inviteWidgetProps}
      />
    </section>
  );
}

export default function UsersPage() {
  const { session } = useAuth();
  const [activeTab, setActiveTab] = useState("CUSTOMER_MANAGER");
  const [activeState, setActiveState] = useState("LINKED");
  const managerInviteRef = useRef(null);
  const technicianInviteRef = useRef(null);
  const operatorInviteRef = useRef(null);
  const [toolbarSearchTerms, setToolbarSearchTerms] = useState({});
  const refreshHandlersRef = useRef({});

  const customerId = useMemo(() => {
    const fromSession =
      session?.customerId ??
      session?.customer_id ??
      session?.customer_id_fk ??
      session?.custid;
    const fromLS = Number(localStorage.getItem("customerId") || 0);
    return Number(fromSession || fromLS || 0);
  }, [
    session?.customerId,
    session?.customer_id,
    session?.customer_id_fk,
    session?.custid,
  ]);

  const customerName = resolveActiveCustomerContext(session).customerName;

  const activeRoleLabel = customerUserRoleLabel(activeTab);
  const activeRolePluralLabel = customerUserRoleLabel(activeTab, { plural: true });
  const activeInviteRole = activeTab;
  const activeInviteWidgetRef =
    activeTab === "CUSTOMER_MANAGER"
      ? managerInviteRef
      : activeTab === "TECHNICIAN"
        ? technicianInviteRef
        : operatorInviteRef;
  const activeStateLinkedLabel = `Linked ${activeRolePluralLabel}`;
  const activeToolbarKey = `${activeInviteRole}-${activeState}`;
  const activeSearchPlaceholder =
    activeState === "PENDING"
      ? "Search invites"
      : `Search ${activeRolePluralLabel.toLowerCase()}`;
  const activeSearchAriaLabel =
    activeState === "PENDING"
      ? `Search pending ${activeRoleLabel.toLowerCase()} invites`
      : `Search linked ${activeRoleLabel.toLowerCase()}s`;
  const activeToolbarSearchTerm = toolbarSearchTerms[activeToolbarKey] ?? "";
  const setActiveToolbarSearchTerm = (value) => {
    setToolbarSearchTerms((current) => ({
      ...current,
      [activeToolbarKey]: value,
    }));
  };
  const registerRefreshHandler = (key) => (handler) => {
    refreshHandlersRef.current[key] = handler;
  };
  const refreshActiveDataView = () => {
    refreshHandlersRef.current[activeToolbarKey]?.();
  };

  if (session?.canManageCustomerUsers !== true) {
    return null;
  }

  return (
    <DashboardPageShell
      title={customerName}
      productDashboardShell="customer"
      shellClassName="customer-users-page-shell"
    >
      <DashboardPanel className="customer-users-page-panel">
        <header className="dashboard-header customer-users-header">
          <div className="dashboard-header-text">
            <h1 className="dashboard-title">Account Users</h1>
            <p className="dashboard-subtitle">
              Manage Fleet Manager, operator, and technician access for your account.
            </p>
          </div>
          <div className="customer-users-header-actions">
            <div
              className="fleet-toggle customer-users-tabs"
              role="tablist"
              aria-label="Account user role sections"
            >
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "CUSTOMER_MANAGER"}
                onClick={() => {
                  setActiveTab("CUSTOMER_MANAGER");
                  setActiveState("LINKED");
                }}
                className="fleet-toggle-button"
              >
                Fleet Managers
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "OPERATOR"}
                onClick={() => {
                  setActiveTab("OPERATOR");
                  setActiveState("LINKED");
                }}
                className="fleet-toggle-button"
              >
                Operators
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "TECHNICIAN"}
                onClick={() => {
                  setActiveTab("TECHNICIAN");
                  setActiveState("LINKED");
                }}
                className="fleet-toggle-button"
              >
                Technicians
              </button>
            </div>
            <DashboardPrimaryActionButton
              htmlType="button"
              type="default"
              className="dashboard-secondary-action-button customer-users-desktop-page-action"
              onClick={() => activeInviteWidgetRef.current?.openInviteModal()}
            >
              Invite {activeRoleLabel}
            </DashboardPrimaryActionButton>
          </div>
        </header>

        <div className="selected-unit-record-controls customer-users-state-toolbar">
          <Segmented
            className="selected-unit-record-filter customer-users-state-filters"
            aria-label="Account user state filters"
            options={[
              { label: activeStateLinkedLabel, value: "LINKED" },
              { label: "Pending Invites", value: "PENDING" },
            ]}
            value={activeState}
            onChange={setActiveState}
          />
          <Space
            className="dashboard-fleet-table-controls customer-users-toolbar customer-users-state-toolbar-controls"
            wrap
          >
            <DashboardSearchInput
              value={activeToolbarSearchTerm}
              onChange={(e) => setActiveToolbarSearchTerm(e.target.value)}
              placeholder={activeSearchPlaceholder}
              ariaLabel={activeSearchAriaLabel}
              className="dashboard-fleet-search maintenance-list-search customer-users-search"
            />
            <DashboardRefreshButton
              onClick={refreshActiveDataView}
              className="maintenance-list-refresh customer-users-refresh"
            />
          </Space>
        </div>

        <div className="customer-users-content">
          <InviteBlock
            customerId={customerId}
            role={activeInviteRole}
            inviteRef={activeInviteWidgetRef}
            showContent={activeState === "PENDING"}
            inviteWidgetProps={{
              searchTerm:
                toolbarSearchTerms[`${activeInviteRole}-PENDING`] ?? "",
              onSearchTermChange: (value) =>
                setToolbarSearchTerms((current) => ({
                  ...current,
                  [`${activeInviteRole}-PENDING`]: value,
                })),
              onRefreshReady: registerRefreshHandler(
                `${activeInviteRole}-PENDING`,
              ),
              hideDesktopToolbar: true,
            }}
          />
          {activeState === "LINKED" && (
            <LinkedRolesWidget
              customerId={customerId}
              role={activeInviteRole}
              searchTerm={
                toolbarSearchTerms[`${activeInviteRole}-LINKED`] ?? ""
              }
              onSearchTermChange={(value) =>
                setToolbarSearchTerms((current) => ({
                  ...current,
                  [`${activeInviteRole}-LINKED`]: value,
                }))
              }
              onRefreshReady={registerRefreshHandler(
                `${activeInviteRole}-LINKED`,
              )}
              hideDesktopToolbar
            />
          )}
        </div>
      </DashboardPanel>
    </DashboardPageShell>
  );
}
